// One enforcement notice or prosecution in full (Session AON). Office/admin. The facts, the
// forward-only status workflow as BUTTONS (never a free dropdown, matching the accident/internal-audit
// pattern), the remedial action CHAS Q94 asks for, the filed document, and the delete control.
import { useCallback, useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, ApiError, type EnforcementActionDetail, type EnforcementActionStatus, type EnforcementActionType } from "../../lib/api";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { RecordHeader } from "../../components/RecordHeader";
import { StatusPill } from "../../components/StatusPill";
import { formatDate } from "../../lib/format";
import { ACTION_TYPES, ACTION_TYPE_LABEL, STATUS_LABEL, toDateInputValue } from "./enforcementActionsUi";

const inputCls =
  "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green disabled:opacity-50";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";
const sectionCls = "mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5";

export function EnforcementActionDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState<EnforcementActionDetail | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "notfound" | "error">("loading");

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!opts?.silent) setState("loading");
      try {
        setRecord(await api.enforcementActions.get(id));
        setState("ready");
      } catch (err) {
        if (!opts?.silent) setState(err instanceof ApiError && err.status === 404 ? "notfound" : "error");
      }
    },
    [id],
  );
  const refresh = useCallback(() => load({ silent: true }), [load]);
  useEffect(() => {
    void load();
  }, [load]);

  if (state === "loading") return <Loader shape="panel" className="p-6" />;
  if (state === "notfound") return <p className="p-6 text-center text-ele-body">That record could not be found.</p>;
  if (state === "error" || !record)
    return (
      <p role="alert" className="p-6 text-center text-ele-strong">
        Could not load this record. Please try again.
      </p>
    );

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <RecordHeader
        className="mb-4"
        title={record.ref}
        backTo="/office/enforcement-actions"
        backLabel="Back to the register"
        status={
          <>
            <StatusPill domain="enforcement" status={record.status} />
            <span className="text-sm text-ele-body">{ACTION_TYPE_LABEL[record.actionType] ?? record.actionType}</span>
            <span className="text-sm text-ele-body">{formatDate(record.dateIssued)}</span>
          </>
        }
      />

      <StatusPanel record={record} onChanged={refresh} />
      <DetailsPanel record={record} onSaved={refresh} />
      <RemedialActionPanel record={record} onSaved={refresh} />
      <DocumentPanel record={record} onChanged={refresh} />
      <DeleteSection record={record} onDeleted={() => navigate("/office/enforcement-actions")} />
    </div>
  );
}

// Forward-only, as BUTTONS rather than a dropdown, matching the accident/internal-audit pattern: the
// office moves a notice on, it never sets a status arbitrarily.
function StatusPanel({ record, onChanged }: { record: EnforcementActionDetail; onChanged: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function move(status: EnforcementActionStatus) {
    setBusy(true);
    setError(null);
    try {
      await api.enforcementActions.update(record.id, { status });
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "Could not update the status.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={sectionCls}>
      <h2 className="mb-2 font-heading text-lg text-ele-strong">Status</h2>
      {record.allowedNextStatuses.length === 0 ? (
        <p className="text-sm text-ele-body">This notice is closed. No further status changes are possible.</p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-ele-body">Move forward to:</span>
          {record.allowedNextStatuses.map((s) => (
            <button
              key={s}
              disabled={busy}
              onClick={() => move(s)}
              className="rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green disabled:opacity-40"
            >
              {STATUS_LABEL[s]}
            </button>
          ))}
        </div>
      )}
      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
    </section>
  );
}

function DetailsPanel({ record, onSaved }: { record: EnforcementActionDetail; onSaved: () => Promise<void> }) {
  const [form, setForm] = useState(() => seedDetails(record));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setForm(seedDetails(record));
  }, [record]);

  const set =
    (name: keyof ReturnType<typeof seedDetails>) =>
    (e: { target: { value: string } }) => {
      setForm((f) => ({ ...f, [name]: e.target.value }));
      setSaved(false);
    };

  async function save(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.enforcementActions.update(record.id, {
        actionType: form.actionType as EnforcementActionType,
        issuingBody: form.issuingBody.trim(),
        dateIssued: form.dateIssued || undefined,
        description: form.description.trim(),
      });
      setSaved(true);
      await onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className={sectionCls}>
      <h2 className="mb-3 font-heading text-lg text-ele-strong">The notice</h2>
      <form onSubmit={save} className="grid gap-3 sm:grid-cols-2">
        <label>
          <span className={labelCls}>Type</span>
          <select aria-label="Type" value={form.actionType} onChange={set("actionType")} className={inputCls}>
            {ACTION_TYPES.map((t) => (
              <option key={t} value={t}>
                {ACTION_TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className={labelCls}>Date issued</span>
          <input aria-label="Date issued" type="date" value={form.dateIssued} onChange={set("dateIssued")} className={inputCls} />
        </label>
        <label className="sm:col-span-2">
          <span className={labelCls}>Issued by</span>
          <input aria-label="Issued by" value={form.issuingBody} onChange={set("issuingBody")} className={inputCls} />
        </label>
        <label className="sm:col-span-2">
          <span className={labelCls}>What it concerned</span>
          <textarea aria-label="What it concerned" value={form.description} onChange={set("description")} rows={4} className={inputCls} />
        </label>

        <div className="sm:col-span-2 flex items-center gap-3">
          <Button type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
          {saved && (
            <span role="status" className="text-sm text-ele-green">
              Saved.
            </span>
          )}
        </div>
      </form>
      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
    </section>
  );
}

// CHAS Q94's own second question: "the remedial actions which have been taken to prevent a
// reoccurrence." First-class fields, not buried in free-text notes.
function RemedialActionPanel({ record, onSaved }: { record: EnforcementActionDetail; onSaved: () => Promise<void> }) {
  const [form, setForm] = useState(() => seedRemedial(record));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setForm(seedRemedial(record));
  }, [record]);

  const set =
    (name: keyof ReturnType<typeof seedRemedial>) =>
    (e: { target: { value: string } }) => {
      setForm((f) => ({ ...f, [name]: e.target.value }));
      setSaved(false);
    };

  async function save(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.enforcementActions.update(record.id, {
        remedialAction: form.remedialAction.trim() || null,
        remedialActionCompletedAt: form.remedialActionCompletedAt || null,
        notes: form.notes.trim() || null,
      });
      setSaved(true);
      await onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className={sectionCls}>
      <h2 className="mb-1 font-heading text-lg text-ele-strong">Remedial action</h2>
      <p className="mb-3 text-xs text-ele-body opacity-80">What was done to prevent a reoccurrence. CHAS asks for this explicitly.</p>
      <form onSubmit={save} className="grid gap-3 sm:grid-cols-2">
        <label className="sm:col-span-2">
          <span className={labelCls}>Remedial action taken</span>
          <textarea aria-label="Remedial action taken" value={form.remedialAction} onChange={set("remedialAction")} rows={3} className={inputCls} />
        </label>
        <label>
          <span className={labelCls}>Completed on</span>
          <input
            aria-label="Remedial action completed on"
            type="date"
            value={form.remedialActionCompletedAt}
            onChange={set("remedialActionCompletedAt")}
            className={inputCls}
          />
        </label>
        <label className="sm:col-span-2">
          <span className={labelCls}>Notes</span>
          <textarea aria-label="Notes" value={form.notes} onChange={set("notes")} rows={2} className={inputCls} />
        </label>

        <div className="sm:col-span-2 flex items-center gap-3">
          <Button type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
          {saved && (
            <span role="status" className="text-sm text-ele-green">
              Saved.
            </span>
          )}
        </div>
      </form>
      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
    </section>
  );
}

function DocumentPanel({ record, onChanged }: { record: EnforcementActionDetail; onChanged: () => Promise<void> }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function onPick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // let the same file be re-selected later
    if (!file) return;
    setError(null);
    const allowed = ["application/pdf", "image/jpeg", "image/png"];
    if (!allowed.includes(file.type)) {
      setError("Upload a PDF, JPG or PNG.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("That file is over 10MB. Please upload a smaller one.");
      return;
    }
    setUploading(true);
    try {
      await api.enforcementActions.uploadDocument(record.id, file, file.name);
      await onChanged();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.code === "drive_not_connected"
            ? "Google Drive is not connected, so the document could not be filed."
            : err.message || err.code
          : "Could not upload the document. Please try again.",
      );
    } finally {
      setUploading(false);
    }
  }

  return (
    <section className={sectionCls}>
      <h2 className="mb-2 font-heading text-lg text-ele-strong">The notice or judgment document</h2>
      <p className="mb-3 text-sm text-ele-body">The notice or judgment itself, filed to Google Drive.</p>
      <div className="flex flex-wrap items-center gap-3">
        {record.hasDocument && (
          <a
            href={api.enforcementActions.documentDownloadHref(record.id)}
            target="_blank"
            rel="noopener noreferrer"
            className="font-menu text-xs tracking-wide text-ele-blue underline"
          >
            View document
          </a>
        )}
        <input ref={fileRef} type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" onChange={onPick} />
        <button
          type="button"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
          className="rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:border-ele-green hover:text-ele-green disabled:opacity-40"
        >
          {uploading ? "Uploading..." : record.hasDocument ? "Replace document" : "Upload document"}
        </button>
      </div>
      {error && (
        <p role="alert" className="mt-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
    </section>
  );
}

function DeleteSection({ record, onDeleted }: { record: EnforcementActionDetail; onDeleted: () => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Mirror the server rule exactly (RULE 1): a notice nothing has been done to hard-deletes; anything
  // carrying a remedial action, a filed document, or that has progressed is archived (closed) and kept.
  const willArchive = record.status !== "open" || Boolean(record.remedialAction && record.remedialAction.trim()) || record.hasDocument;

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await api.enforcementActions.remove(record.id);
      onDeleted();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "Could not delete this record.");
      setBusy(false);
    }
  }

  return (
    <section className="rounded-ele border border-ele-hairline bg-ele-surface-soft p-5">
      <Button
        variant="destructive"
        size="sm"
        onClick={() => {
          setError(null);
          setOpen(true);
        }}
      >
        {willArchive ? "Archive record" : "Delete record"}
      </Button>
      <ConfirmDeleteModal
        open={open}
        variant={willArchive ? "archive" : "destructive"}
        title={willArchive ? "Archive this record?" : "Delete this record?"}
        message={
          willArchive
            ? "This record carries a remedial action, a filed document, or has progressed, so it will be archived (closed) and kept in full for the statutory record, not deleted. There is no restore once archived."
            : "Nothing has been recorded against this beyond the initial report, so it will be permanently deleted. This cannot be undone."
        }
        confirmLabel={willArchive ? "Archive record" : "Delete record"}
        busy={busy}
        error={error}
        onConfirm={confirm}
        onCancel={() => {
          if (!busy) {
            setOpen(false);
            setError(null);
          }
        }}
      />
    </section>
  );
}

function seedDetails(r: EnforcementActionDetail) {
  return {
    actionType: r.actionType as string,
    issuingBody: r.issuingBody,
    dateIssued: toDateInputValue(r.dateIssued),
    description: r.description,
  };
}

function seedRemedial(r: EnforcementActionDetail) {
  return {
    remedialAction: r.remedialAction ?? "",
    remedialActionCompletedAt: toDateInputValue(r.remedialActionCompletedAt),
    notes: r.notes ?? "",
  };
}
